import { BrandPageQueryObj } from "@faire/retailer-shared/search/BrandPageQueryObj";
import { IBrandProductsGridBanner } from "@faire/web-api--source/indigofair/data/IBrandProductsGridBanner";
import { ICategory } from "@faire/web-api--source/indigofair/data/ICategory";
import { IFilterSection } from "@faire/web-api--source/indigofair/data/IFilterSection";
import { IProductBasedCollectionMetadata } from "@faire/web-api--source/indigofair/data/IProductBasedCollectionMetadata";
import { IProductCollectionMetadataResponse } from "@faire/web-api--source/indigofair/data/IProductCollectionMetadataResponse";
import { ISearchBrandLeanProductTilesResponse } from "@faire/web-api--source/indigofair/data/ISearchBrandLeanProductTilesResponse";
import { ISearchBrandProductsFiltersResponse } from "@faire/web-api--source/indigofair/data/ISearchBrandProductsFiltersResponse";
import { ProductBasedCollectionState } from "@faire/web-api--source/indigofair/data/ProductBasedCollectionState";

import { ILeanProductTile } from "@faire/retailer/features/MarketplaceItems/MarketplaceProduct/LeanProtos/ILeanProductTile";
import { getCollectionHidingReasons } from "@faire/retailer/views/brand/utils/CollectionHidingConditions";

export interface IProductsData {
  pageLoadKey: string;
  isLoading: boolean;
  productSearchRequestId: string | undefined;
  pageCount: number;
  productTiles: ReadonlyArray<ILeanProductTile>;
  productBasedCollections: ReadonlyArray<IProductBasedCollectionMetadata>;
  filterSectionsAll: ReadonlyArray<IFilterSection>;
  collectionsCount: number;
  collectionHidingReasons: string;
  totalResults: number;
  currentlySelectedPreorderSeasonName: string | undefined;
  showVerticalImagery: boolean;
  productGridBanner: IBrandProductsGridBanner | undefined;
  dataUpdatedAt: number;
}

export const productsDataAggregation = (options: {
  queryParams: BrandPageQueryObj;
  categories?: Array<ICategory> | undefined;
  collectionsData?: IProductCollectionMetadataResponse | undefined;
  collectionsQueryKey?: object | undefined;
  collectionsQueryPending?: boolean;
  collectionsQueryDataUpdatedAt?: number;
  productsData?: ISearchBrandLeanProductTilesResponse | undefined;
  productsQueryKey?: object | undefined;
  productsQueryPending?: boolean;
  productsQueryDataUpdatedAt?: number;
  filtersData?: ISearchBrandProductsFiltersResponse | undefined;
}): IProductsData => {
  const {
    queryParams,
    categories,
    collectionsData,
    collectionsQueryKey,
    collectionsQueryPending,
    collectionsQueryDataUpdatedAt,
    productsData,
    productsQueryKey,
    productsQueryPending,
    productsQueryDataUpdatedAt,
    filtersData,
  } = options;
  const collectionHidingReasons = getCollectionHidingReasons(
    queryParams,
    categories
  );

  let pageCount = 0;
  if (queryParams.collections) {
    pageCount = collectionsData?.pagination_data?.page_count ?? 1;
  } else if (!queryParams.collections && productsData) {
    pageCount = productsData?.pagination_data?.page_count ?? 1;
  }

  const collectionsCount =
    collectionsData?.states_to_counts?.[
      ProductBasedCollectionState.PUBLISHED
    ] ?? 0;

  const queryKeyString = JSON.stringify([
    collectionsQueryKey,
    productsQueryKey,
  ]);

  return {
    pageLoadKey: `${queryKeyString},${
      // Include queryParams.collections to indicate that switching to/from collections view
      // should trigger a new UPLT event (even though it doesn't trigger a new request).
      Boolean(queryParams.collections)
    }`,
    dataUpdatedAt: Math.max(
      collectionsQueryDataUpdatedAt ?? 0,
      productsQueryDataUpdatedAt ?? 0
    ),
    isLoading: collectionsQueryPending || productsQueryPending || false,
    currentlySelectedPreorderSeasonName:
      filtersData?.currently_selected_category?.season_year_name,
    filterSectionsAll: filtersData?.filter_sections ?? FROZEN_EMPTY_ARRAY,
    totalResults: filtersData?.total_products_count ?? 0,
    showVerticalImagery: !!productsData?.should_show_vertical_image,
    productSearchRequestId: productsData?.request_id ?? "",
    pageCount,
    collectionsCount,
    collectionHidingReasons: collectionHidingReasons.toString(),
    productTiles: queryParams.collections
      ? FROZEN_EMPTY_ARRAY
      : (productsData?.product_tiles ?? FROZEN_EMPTY_ARRAY),
    productBasedCollections:
      collectionsData?.product_based_collections ?? FROZEN_EMPTY_ARRAY,
    productGridBanner: filtersData?.banner,
  };
};

export const FROZEN_EMPTY_ARRAY: ReadonlyArray<never> = Object.freeze([]);
